import React, { Dispatch, SetStateAction, memo, useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { DerivedValue, SharedValue } from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { ReanimatedAccordion } from '@/components/ReanimatedAccordion';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { checkIfRegularProjection } from '@/feature/lobby/utils/lineupsUtils';
import {
    MIN_TOTAL_HEIGHT_CHART,
    PlayerRecentStats,
} from '@/feature/player-profile-sheets/components/PlayerRecentStats';
import { LIST_OF_ALTERNATE_PROJECTIONS_HEIGHT } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import {
    getArrayOfPlayerRecentStats,
    getShouldShowPlayerRecentStats,
} from '@/feature/player-profile-sheets/utils/player-recent-stats-utils';
import { common, withOpacity } from '@/styles/styles';
import { League, PlayerRecentStat } from '@/types/api.generated';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { isProjectionSelected } from '@/utils/projections';

import { AlternateProjection } from '../AlternateProjection';

export const AlternateProjectionRowExpandedContent = memo(
    ({
        setActiveAlternateIndex,
        activeAlternateIndex,
        expanded,
        projectionsGroup,
        rowIndex,
        expandedIndex,
        playerPicked,
        playerRecentStats,
        league,
    }: {
        setActiveAlternateIndex: Dispatch<SetStateAction<number>>;
        activeAlternateIndex: number;
        expanded: DerivedValue<boolean>;
        projectionsGroup: PlayerProjectionFragment[];
        rowIndex: number;
        expandedIndex: SharedValue<number>;
        playerPicked?: BetslipPick;
        playerRecentStats?: PlayerRecentStat[];
        league: League;
    }) => {
        const scrollRef = useRef<FlatList>(null);
        const onViewableItemsChanged = useCallback(() => {
            // this is a workaround for the FlatList to scroll to the selected item
            // when the user opens the accordion
            // as initialScrollIndex was causing the list to disappear
            if (expandedIndex.value === null || expandedIndex.value !== rowIndex) {
                scrollRef.current?.scrollToIndex({
                    index: activeAlternateIndex,
                    animated: true,
                    viewPosition: 0.5, // centers the selected item
                    viewOffset: 0,
                });
            }
        }, [activeAlternateIndex, expandedIndex.value, rowIndex]);
        const viewabilityConfigCallbackPairs = useRef([
            // there's a crash on web if we don't use a ref for the viewabilityConfigCallbackPairs
            // viewAreaCoveragePercentThreshold: 10% of the item needs to be visible so that the item is considered viewable
            { onViewableItemsChanged, viewabilityConfig: { viewAreaCoveragePercentThreshold: 10 } },
        ]);
        const playerStats = getArrayOfPlayerRecentStats(playerRecentStats);
        const showPlayerRecentStats = getShouldShowPlayerRecentStats({ projectionsGroup, league });
        const showListOfAlternateProjections = projectionsGroup.length > 1;
        let contentHeight;
        if (!showPlayerRecentStats) {
            contentHeight = LIST_OF_ALTERNATE_PROJECTIONS_HEIGHT;
        } else if (showListOfAlternateProjections) {
            contentHeight = LIST_OF_ALTERNATE_PROJECTIONS_HEIGHT + MIN_TOTAL_HEIGHT_CHART;
        } else {
            contentHeight = MIN_TOTAL_HEIGHT_CHART;
        }

        useEffect(() => {
            if (!showPlayerRecentStats && !showListOfAlternateProjections) {
                // if there's nothing to show, we collapse the accordion
                expandedIndex.value = -1;
            }
        }, [expandedIndex, showListOfAlternateProjections, showPlayerRecentStats]);

        const values = playerStats.map(s => s.value).filter(v => v !== undefined);
        // fallback to 1 to avoid division by zero
        const maxValue = values.length > 0 ? Math.max(...values) : 1;
        const selectedProjection = projectionsGroup[activeAlternateIndex];
        const selectedProjectionValue = checkIfRegularProjection(selectedProjection.type)
            ? selectedProjection.value
            : selectedProjection.nonRegularValue ?? 0;

        return (
            <ReanimatedAccordion expanded={expanded} contentHeight={contentHeight}>
                <Box pt={'s4'} pb={'s16'} justifyContent={'center'}>
                    <Box pointerEvents={'none'} zIndex={1}>
                        {showListOfAlternateProjections ? (
                            <>
                                <LinearGradient
                                    colors={[
                                        ds.colorValue.background.neutral.subtler,
                                        withOpacity(ds.colorValue.background.neutral.subtler, 0),
                                    ]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    width={24}
                                    height={36}
                                    style={styles.gradient}
                                />
                                <LinearGradient
                                    colors={[
                                        ds.colorValue.background.neutral.subtler,
                                        withOpacity(ds.colorValue.background.neutral.subtler, 0),
                                    ]}
                                    start={{ x: 1, y: 0 }}
                                    end={{ x: 0, y: 0 }}
                                    width={24}
                                    height={36}
                                    style={[styles.gradient, styles.right]}
                                />
                            </>
                        ) : null}
                    </Box>
                    {showListOfAlternateProjections ? (
                        <FlatList<PlayerProjectionFragment>
                            keyExtractor={(item, pIndex) => `${pIndex}-${item.marketId}`}
                            data={projectionsGroup}
                            viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs.current}
                            onScrollToIndexFailed={error => {
                                // if the scrollToIndex fails, we scroll to the furthest offset
                                scrollRef.current?.scrollToOffset({
                                    offset: error.averageItemLength * error.index,
                                    animated: true,
                                });
                            }}
                            renderItem={({ item: alternateProjection, index: pIndex }) => {
                                const isSelected = isProjectionSelected(alternateProjection, playerPicked);
                                const showActiveDot = isSelected && activeAlternateIndex !== pIndex;
                                return (
                                    <AlternateProjection
                                        showActiveDot={showActiveDot}
                                        alternateProjection={alternateProjection}
                                        isActive={activeAlternateIndex === pIndex}
                                        setActiveAlternateIndex={() => {
                                            setActiveAlternateIndex(pIndex);
                                            scrollRef.current?.scrollToIndex({
                                                index: pIndex,
                                                animated: true,
                                                viewPosition: 0.5, // centers the selected item
                                                viewOffset: 0,
                                            });
                                        }}
                                    />
                                );
                            }}
                            ref={scrollRef}
                            horizontal
                            contentContainerStyle={[common.paddingHorizontal, styles.listContent]}
                            showsHorizontalScrollIndicator={showScrollIndicator}
                        />
                    ) : null}
                    {showPlayerRecentStats ? (
                        <PlayerRecentStats
                            activeAlternateIndex={activeAlternateIndex}
                            playerStats={playerStats}
                            selectedProjectionValue={selectedProjectionValue}
                            maxValue={maxValue}
                        />
                    ) : null}
                </Box>
            </ReanimatedAccordion>
        );
    }
);

const styles = StyleSheet.create({
    gradient: {
        position: 'absolute',
        width: 24,
        height: 36,
        zIndex: 1,
    },
    right: {
        right: 0,
    },
    alternateProjectionCard: { paddingHorizontal: 13, paddingVertical: 7, marginHorizontal: 5 },
    listContent: { gap: 10, paddingBottom: 16 },
});
